import React from "react";
import { View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import {
  ContentActions,
  APPROVE_ACTION_COLORS,
  REJECT_ACTION_COLORS,
} from "@/components/ui/ContentActions";
import { Icon } from "@/components/ui/Icon";
import { Pill } from "@/components/ui/Pill";
import { Feather } from "@expo/vector-icons";
import type { ProductCompliance, ComplianceCertificate } from "@/lib/api/review";
import {
  FieldRow,
  Divider,
  FieldDecisionActions,
  type FieldDecision,
  type RejectFieldMeta,
  type FieldRejectionData,
  type EditFieldMeta,
  type FieldEditData,
} from "./Shared";
import { FilePreview } from "@/components/ui/FilePreview";
import { useLocalizedNames } from "@/hooks/useLocalizedNames";

interface ProductComplianceSectionProps {
  compliance: ProductCompliance | undefined;
  fieldDecisions: Record<string, FieldDecision>;
  fieldRejectionData: Record<string, FieldRejectionData>;
  onDecide: (fieldKey: string, decision: FieldDecision) => void;
  onRejectField: (fieldKey: string, meta: RejectFieldMeta) => void;
  onUndoDecision: (fieldKey: string) => void;
  onShowReason: (fieldKey: string) => void;
  fieldEditData: Record<string, FieldEditData>;
  onEditField: (fieldKey: string, meta: EditFieldMeta) => void;
  onShowEdit: (fieldKey: string) => void;
  t: ReturnType<typeof import("@/context/I18nContext").useI18n>["t"];
  applicationId: string;
}

export function ProductComplianceSection({
  compliance,
  fieldDecisions,
  fieldRejectionData: _fieldRejectionData,
  onDecide,
  onRejectField,
  onUndoDecision,
  onShowReason,
  fieldEditData: _fieldEditData,
  onEditField: _onEditField,
  onShowEdit,
  applicationId,
  t,
}: ProductComplianceSectionProps) {
  const notUploaded = t("review.application.legalRepresentative.notUploaded");
  const { resolveIndustryName, resolveCategoryName } = useLocalizedNames(
    compliance?.operatingIndustries ?? [],
    compliance?.operatingCategories ?? []
  );

  /** Renders a single-cert field with optional ContentActions. */
  function renderSingleCert(
    fieldKey: string,
    cert: ComplianceCertificate | undefined,
    fieldLabel: string,
    applicationId: string
  ) {
    return (
      <View className="flex-1 items-start gap-3">
        {cert ? (
          <>
            <FilePreview fileKey={cert.fileUrl} isVendor={false} applicationId={applicationId} />
            {fieldDecisions[fieldKey] ? (
              <FieldDecisionActions
                fieldKey={fieldKey}
                decision={fieldDecisions[fieldKey]}
                onUndoDecision={onUndoDecision}
                onShowReason={onShowReason}
                onShowEdit={onShowEdit}
                t={t}
              />
            ) : (
              <ContentActions
                actions={[
                  {
                    label: "review.application.approve",
                    onPress: () => onDecide(fieldKey, "approve"),
                    icon: (
                      <Icon as={Feather} name="check-circle" size={14} className="text-green-50" />
                    ),
                    ...APPROVE_ACTION_COLORS,
                  },
                  {
                    label: "review.application.reject",
                    onPress: () =>
                      onRejectField(fieldKey, { label: fieldLabel, value: cert?.fileName }),
                    icon: <Icon as={Feather} name="x" size={14} className="text-error" />,
                    ...REJECT_ACTION_COLORS,
                  },
                ]}
              />
            )}
          </>
        ) : (
          <AppText className="text-lg text-dark-text-10 italic">{notUploaded}</AppText>
        )}
      </View>
    );
  }

  /** Renders a list-cert field, each item with its own ContentActions. */
  function renderCertList(
    fieldKey: string,
    certs: ComplianceCertificate[] | undefined,
    fieldLabel: string,
    applicationId: string
  ) {
    if (!certs || certs.length === 0) {
      return (
        <View className="flex-1">
          <AppText className="text-lg text-dark-text-10 italic">{notUploaded}</AppText>
        </View>
      );
    }
    return (
      <View className="flex-1 items-start gap-6">
        {certs.map((cert, i) => {
          const key = `${fieldKey}_${i}`;
          return (
            <View key={key} className="gap-3">
              <FilePreview fileKey={cert.fileUrl} isVendor={false} applicationId={applicationId} />
              {fieldDecisions[key] ? (
                <FieldDecisionActions
                  fieldKey={key}
                  decision={fieldDecisions[key]}
                  onUndoDecision={onUndoDecision}
                  onShowReason={onShowReason}
                  onShowEdit={onShowEdit}
                  t={t}
                />
              ) : (
                <ContentActions
                  actions={[
                    {
                      label: "review.application.approve",
                      onPress: () => onDecide(key, "approve"),
                      icon: (
                        <Icon
                          as={Feather}
                          name="check-circle"
                          size={14}
                          className="text-green-50"
                        />
                      ),
                      ...APPROVE_ACTION_COLORS,
                    },
                    {
                      label: "review.application.reject",
                      onPress: () =>
                        onRejectField(key, { label: fieldLabel, value: cert?.fileName }),
                      icon: <Icon as={Feather} name="x" size={14} className="text-error" />,
                      ...REJECT_ACTION_COLORS,
                    },
                  ]}
                />
              )}
            </View>
          );
        })}
      </View>
    );
  }

  return (
    <View className="items-start w-full">
      {/* Card header */}
      <View className="px-6 py-5 border-b border-stroke-50 items-start w-full">
        <AppText weight="semibold" className="text-lg text-dark-text-50">
          {t("review.application.productCompliance.title")}
        </AppText>
      </View>

      {/* Card body */}
      <View className="px-8 py-6 w-full">
        {/* Operating Industries */}
        <View className="gap-3 items-start w-full">
          <AppText weight="semibold" className="text-base text-supporting-text-40">
            {t("review.application.productCompliance.fields.operatingIndustries")}
          </AppText>
          <View className="flex-row flex-wrap gap-2">
            {(compliance?.operatingIndustries ?? []).map((industry) => (
              <Pill key={industry} plainLabel={resolveIndustryName(industry)} variant="tag" />
            ))}
          </View>
          {fieldDecisions["operatingIndustries"] ? (
            <FieldDecisionActions
              fieldKey="operatingIndustries"
              decision={fieldDecisions["operatingIndustries"]}
              onUndoDecision={onUndoDecision}
              onShowReason={onShowReason}
              onShowEdit={onShowEdit}
              t={t}
            />
          ) : (
            <ContentActions
              actions={[
                {
                  label: "review.application.approve",
                  onPress: () => onDecide("operatingIndustries", "approve"),
                  icon: (
                    <Icon as={Feather} name="check-circle" size={14} className="text-green-50" />
                  ),
                  ...APPROVE_ACTION_COLORS,
                },
                {
                  label: "review.application.reject",
                  onPress: () =>
                    onRejectField("operatingIndustries", {
                      label: t("review.application.productCompliance.fields.operatingIndustries"),
                      value: compliance?.operatingIndustries?.map(resolveIndustryName).join(", "),
                    }),
                  icon: <Icon as={Feather} name="x" size={14} className="text-error" />,
                  ...REJECT_ACTION_COLORS,
                },
              ]}
            />
          )}
        </View>

        <Divider />

        {/* Operating Categories */}
        <View className="gap-3 items-start w-full">
          <AppText weight="semibold" className="text-base text-supporting-text-40">
            {t("review.application.productCompliance.fields.operatingCategories")}
          </AppText>
          <View className="flex-row flex-wrap gap-2">
            {(compliance?.operatingCategories ?? []).map((cat) => (
              <Pill key={cat} plainLabel={resolveCategoryName(cat)} variant="tag" />
            ))}
          </View>
          {fieldDecisions["operatingCategories"] ? (
            <FieldDecisionActions
              fieldKey="operatingCategories"
              decision={fieldDecisions["operatingCategories"]}
              onUndoDecision={onUndoDecision}
              onShowReason={onShowReason}
              onShowEdit={onShowEdit}
              t={t}
            />
          ) : (
            <ContentActions
              actions={[
                {
                  label: "review.application.approve",
                  onPress: () => onDecide("operatingCategories", "approve"),
                  icon: (
                    <Icon as={Feather} name="check-circle" size={14} className="text-green-50" />
                  ),
                  ...APPROVE_ACTION_COLORS,
                },
                {
                  label: "review.application.reject",
                  onPress: () =>
                    onRejectField("operatingCategories", {
                      label: t("review.application.productCompliance.fields.operatingCategories"),
                      value: compliance?.operatingCategories?.map(resolveCategoryName).join(", "),
                    }),
                  icon: <Icon as={Feather} name="x" size={14} className="text-error" />,
                  ...REJECT_ACTION_COLORS,
                },
              ]}
            />
          )}
        </View>

        <Divider />

        {/* ISO 9001 Certificate + CE Marking */}
        <FieldRow>
          <View className="flex-1 gap-3 items-start w-full">
            <AppText weight="semibold" className="text-base text-supporting-text-40">
              {t("review.application.productCompliance.fields.isoCertificate")}
            </AppText>
            {renderSingleCert(
              "isoCertificate",
              compliance?.isoCertificate,
              t("review.application.productCompliance.fields.isoCertificate"),
              applicationId
            )}
          </View>
          <View className="flex-1 gap-3 items-start w-full">
            <AppText weight="semibold" className="text-base text-supporting-text-40">
              {t("review.application.productCompliance.fields.ceMarking")}
            </AppText>
            {renderSingleCert(
              "ceMarking",
              compliance?.ceMarking,
              t("review.application.productCompliance.fields.ceMarking"),
              applicationId
            )}
          </View>
        </FieldRow>

        <Divider />

        {/* Quality Test Reports + Eco Certifications */}
        <FieldRow>
          <View className="flex-1 gap-3 items-start w-full">
            <AppText weight="semibold" className="text-base text-supporting-text-40">
              {t("review.application.productCompliance.fields.qualityTestReports")}
            </AppText>
            {renderCertList(
              "qualityTestReports",
              compliance?.qualityTestReports,
              t("review.application.productCompliance.fields.qualityTestReports"),
              applicationId
            )}
          </View>
          <View className="flex-1 gap-3 items-start w-full">
            <AppText weight="semibold" className="text-base text-supporting-text-40">
              {t("review.application.productCompliance.fields.ecoCertifications")}
            </AppText>
            {renderCertList(
              "ecoCertifications",
              compliance?.ecoCertifications,
              t("review.application.productCompliance.fields.ecoCertifications"),
              applicationId
            )}
          </View>
        </FieldRow>
      </View>
    </View>
  );
}
